import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Copy, ToggleLeft, ToggleRight, Pencil, ChevronDown, ChevronUp, Package, Trash2 } from 'lucide-react';
import { MODULE_LABELS, PLAN_GROUPS, LIMIT_LABELS, hydratePlanForm, isUnlimited, UNLIMITED, formatLimit } from '@/lib/planLabels';

// S109 — los mercados donde hoy se vende. El ejemplo es el precio del paquete
// Base, para que se vea de una el orden de magnitud que va en cada casilla.
const MONEDAS_DE_VENTA = [
  { clave: 'USD', etiqueta: 'Dólares (USD)', ejemplo: '50' },
  { clave: 'MXN', etiqueta: 'Pesos mexicanos (MXN)', ejemplo: '950' },
  { clave: 'EUR', etiqueta: 'Euros (EUR)', ejemplo: '45' },
];
import PlanCostSimulator from '@/components/admin/PlanCostSimulator';
import { useAuth } from '@/contexts/AuthContext';

const EMPTY_PLAN = {
  name: '', description: '', vertical_scope: 'all', allowed_verticals: [],
  price_monthly: 0, price_annual: 0, currency: 'MXN', billing_cycle: 'both',
  prices_by_currency: {},
  trial_allowed: false, trial_days: 0, included_modules: [], included_entitlements: {},
  included_limits: {}, available_addons: [], sort_order: 0, is_default: false,
};

const MODULES = Object.keys(MODULE_LABELS);
const VERTICALS = ['beauty', 'health', 'fitness', 'education', 'automotive', 'real_estate', 'professional_services', 'generic'];

export default function PlanBuilder() {
  const { isPlatformOwner } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingPlan, setDeletingPlan] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState(EMPTY_PLAN);
  const [saving, setSaving] = useState(false);
  const [sembrando, setSembrando] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [openGroups, setOpenGroups] = useState(() => new Set(PLAN_GROUPS.filter(g => g.defaultOpen).map(g => g.key)));
  const [advancedGroups, setAdvancedGroups] = useState(() => new Set());
  const [aiModels, setAiModels] = useState([]);


  const toggleGroupOpen = (key) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleGroupAdvanced = (key) => {
    setAdvancedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const fetchPlans = async () => {
    try {
      const { data } = await api.get('/admin/plans/catalog');
      setPlans(data);
      try {
        const m = await api.get('/admin/ai/models');
        setAiModels((m.data || []).filter(x => x.status === 'active' || !x.status));
      } catch { /* catálogo opcional */ }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPlans(); }, []);

  // El catálogo del backend: la lista completa de lo que la plataforma sabe
  // hacer. Se pide una vez y de ahí sale todo lo que esta pantalla no tenía
  // escrito a mano.
  const [catalogo, setCatalogo] = useState([]);
  useEffect(() => {
    api.get('/admin/catalogo-de-capacidades')
      .then(r => setCatalogo(r.data?.modulos || []))
      // Si falla, la pantalla sigue sirviendo con los grupos de siempre. Se
      // avisa porque un catálogo que no carga significa que el dueño estaría
      // guardando planes sin ver la mitad de las casillas.
      .catch(() => toast.error('No se pudo cargar el catálogo de capacidades; faltarán casillas.'));
  }, []);

  // Lo del catálogo que los grupos de arriba no pintan. Se compara contra las
  // claves que PLAN_GROUPS ya muestra, así la sección de abajo se vacía sola
  // conforme cada capacidad se acomode en su grupo.
  const clavesYaVisibles = new Set(
    PLAN_GROUPS.flatMap(g => [
      ...(g.entitlements || []), ...(g.advancedEntitlements || []),
      ...(g.limits || []), ...(g.advancedLimits || []),
    ].map(x => x.key))
  );
  const capacidadesSueltas = catalogo.map(m => ({
    modulo: m.modulo,
    nombre: m.nombre,
    items: [
      ...(m.capacidades || []).map(c => ({ ...c, tipo: 'booleano' })),
      ...(m.limites || []).map(l => ({ ...l, tipo: 'limite' })),
    ].filter(i => !clavesYaVisibles.has(i.clave)
                  // Es una lista de proveedores, no un sí/no: no va como casilla.
                  && i.clave !== 'ai_allowed_providers'),
  })).filter(g => g.items.length > 0);

  // S109 — carga los cuatro paquetes y los tres bloques de minutos de voz.
  // Se puede pulsar las veces que haga falta: no duplica, y no pisa los precios
  // que ya hayas ajustado a mano.
  const sembrarEscalera = async () => {
    setSembrando(true);
    try {
      const { data } = await api.post('/admin/plans/catalog/sembrar-escalera');
      await fetchPlans();
      toast.success(data?.mensaje || 'Catálogo comercial cargado.');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSembrando(false);
    }
  };

  const openCreate = () => {
    setEditingPlan(null);
    const hyd = hydratePlanForm({});
    setForm({ ...EMPTY_PLAN, ...hyd });
    setShowForm(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    const hyd = hydratePlanForm(plan);
    setForm({
      name: plan.name, description: plan.description || '', vertical_scope: plan.vertical_scope,
      allowed_verticals: plan.allowed_verticals || [], price_monthly: plan.price_monthly,
      price_annual: plan.price_annual, currency: plan.currency, billing_cycle: plan.billing_cycle,
      prices_by_currency: plan.prices_by_currency || {},
      trial_allowed: plan.trial_allowed, trial_days: plan.trial_days,
      included_modules: plan.included_modules || [],
      included_entitlements: hyd.included_entitlements,
      included_limits: hyd.included_limits,
      available_addons: plan.available_addons || [],
      sort_order: plan.sort_order, is_default: plan.is_default,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nombre es requerido'); return; }
    setSaving(true);
    try {
      if (editingPlan) {
        await api.put(`/admin/plans/catalog/${editingPlan.id}`, form);
        toast.success('Plan actualizado');
      } else {
        await api.post('/admin/plans/catalog', form);
        toast.success('Plan creado');
      }
      setShowForm(false);
      fetchPlans();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handleDuplicate = async (planId) => {
    try {
      await api.post(`/admin/plans/catalog/${planId}/duplicate`);
      toast.success('Plan duplicado');
      fetchPlans();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleToggleStatus = async (plan) => {
    const newStatus = plan.status === 'active' ? 'inactive' : 'active';
    try {
      await api.patch(`/admin/plans/catalog/${plan.id}/status?status=${newStatus}`);
      toast.success(`Plan ${newStatus === 'active' ? 'activado' : 'desactivado'}`);
      fetchPlans();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleDeletePlan = async (plan) => {
    if (plan.is_default) {
      toast.error('No se puede eliminar el plan predeterminado');
      return;
    }
    const force = window.confirm(
      `¿Eliminar permanentemente el plan "${plan.name}"?\n\nSi tiene suscripciones, se te pedirá forzar la desvinculación.`,
    );
    if (!force) return;
    setDeletingPlan(plan.id);
    try {
      await api.delete(`/admin/plans/catalog/${plan.id}`);
      toast.success('Plan eliminado');
      fetchPlans();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 409 || String(detail).includes('suscripción')) {
        if (window.confirm(`${formatApiError(detail)}\n\n¿Forzar eliminación (desvincula suscripciones)?`)) {
          try {
            await api.delete(`/admin/plans/catalog/${plan.id}`, { params: { force: true } });
            toast.success('Plan eliminado (suscripciones desvinculadas)');
            fetchPlans();
          } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
        }
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setDeletingPlan(null); }
  };

  const toggleModule = (mod) => {
    setForm(f => ({
      ...f,
      included_modules: f.included_modules.includes(mod)
        ? f.included_modules.filter(m => m !== mod)
        : [...f.included_modules, mod],
    }));
  };

  const toggleVertical = (v) => {
    setForm(f => ({
      ...f,
      allowed_verticals: f.allowed_verticals.includes(v)
        ? f.allowed_verticals.filter(x => x !== v)
        : [...f.allowed_verticals, v],
    }));
  };

  const updateLimit = (key, val) => {
    setForm(f => ({ ...f, included_limits: { ...f.included_limits, [key]: val } }));
  };

  const toggleEntitlement = (key) => {
    setForm(f => ({
      ...f,
      included_entitlements: { ...f.included_entitlements, [key]: !f.included_entitlements[key] },
    }));
  };

  const setPlanModel = (modelKey) => {
    setForm(f => ({
      ...f,
      included_entitlements: { ...f.included_entitlements, default_ai_model_key: modelKey || '' },
    }));
  };

  const renderLimitField = (key, label) => {
    const raw = form.included_limits[key];
    const unlimited = isUnlimited(raw);
    return (
      <div key={key} className="flex flex-col gap-1">
        <span className="text-[11px] text-[#64748B]">{label}</span>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            className="h-8 text-xs"
            disabled={unlimited}
            value={unlimited ? '' : (raw === 0 || raw ? raw : '')}
            onChange={e => {
              const n = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
              updateLimit(key, Number.isNaN(n) ? 0 : n);
            }}
            placeholder={unlimited ? '∞' : '0'}
            data-testid={`plan-limit-${key}`}
          />
          <label className="flex items-center gap-1 text-[11px] text-[#64748B] whitespace-nowrap cursor-pointer">
            <input
              type="checkbox"
              checked={unlimited}
              onChange={e => updateLimit(key, e.target.checked ? UNLIMITED : 0)}
              data-testid={`plan-limit-unlimited-${key}`}
            />
            Ilimitado
          </label>
        </div>
      </div>
    );
  };

  if (loading) return <PantallaCargando />;

  if (showForm) {
    return (
      <div data-testid="plan-form">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#0F172A]">{editingPlan ? 'Editar Plan' : 'Nuevo Plan'}</h1>
          <Button variant="outline" size="sm" onClick={() => setShowForm(false)} data-testid="plan-form-cancel">Cancelar</Button>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-6">
          {/* Basic info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-[#64748B] mb-1 block">Nombre *</label>
              <Input data-testid="plan-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Professional" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#64748B] mb-1 block">Moneda</label>
              <Input data-testid="plan-currency" value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-[#64748B] mb-1 block">Precio Mensual</label>
              <Input data-testid="plan-price-monthly" type="number" value={form.price_monthly} onChange={e => setForm(f => ({ ...f, price_monthly: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-[#64748B] mb-1 block">Precio Anual</label>
              <Input data-testid="plan-price-annual" type="number" value={form.price_annual} onChange={e => setForm(f => ({ ...f, price_annual: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div className="md:col-span-2 lg:col-span-3 border border-[#E2E8F0] rounded-lg p-3 bg-[#F8FAFC]">
              <p className="text-xs font-semibold text-[#0F172A] mb-1">Precio en cada moneda</p>
              <p className="text-xs text-[#64748B] mb-3">
                A un cliente de México se le cobra en pesos y a uno de Estados Unidos en
                dólares. Aquí decides el precio de cada mercado <strong>tú</strong>: no es
                una conversión automática, así que tu producto no cambia de precio porque
                se movió el tipo de cambio. Si dejas una moneda vacía, se usa el precio en
                dólares.
              </p>
              <div className="grid grid-cols-3 gap-3">
                {MONEDAS_DE_VENTA.map(m => (
                  <div key={m.clave}>
                    <label className="text-xs font-medium text-[#64748B] mb-1 block">
                      {m.etiqueta}
                    </label>
                    <Input
                      data-testid={`plan-precio-${m.clave}`}
                      type="number"
                      placeholder={m.ejemplo}
                      value={(form.prices_by_currency || {})[m.clave] ?? ''}
                      onChange={e => setForm(f => ({
                        ...f,
                        prices_by_currency: {
                          ...(f.prices_by_currency || {}),
                          [m.clave]: e.target.value,
                        },
                      }))}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#64748B] mb-1 block">Ciclo</label>
              <select data-testid="plan-billing-cycle" className="w-full h-10 px-3 text-sm border border-[#E2E8F0] rounded-md" value={form.billing_cycle} onChange={e => setForm(f => ({ ...f, billing_cycle: e.target.value }))}>
                <option value="both">Mensual + Anual</option>
                <option value="monthly">Solo Mensual</option>
                <option value="annual">Solo Anual</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#64748B] mb-1 block">Orden</label>
              <Input data-testid="plan-sort-order" type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-1 block">Descripción</label>
            <textarea data-testid="plan-description" className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-md resize-none" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>

          {/* Trial */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.trial_allowed} onChange={e => setForm(f => ({ ...f, trial_allowed: e.target.checked }))} />
              Trial permitido
            </label>
            {form.trial_allowed && (
              <div className="flex items-center gap-2">
                <Input data-testid="plan-trial-days" type="number" className="w-20" value={form.trial_days} onChange={e => setForm(f => ({ ...f, trial_days: parseInt(e.target.value) || 0 }))} />
                <span className="text-xs text-[#64748B]">días</span>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm ml-4">
              <input type="checkbox" checked={form.is_default} onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))} />
              Plan por defecto
            </label>
          </div>

          {/* Vertical scope */}
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-2 block">Alcance Vertical</label>
            <div className="flex gap-3 mb-2">
              <label className="flex items-center gap-1.5 text-sm"><input type="radio" name="vs" checked={form.vertical_scope === 'all'} onChange={() => setForm(f => ({ ...f, vertical_scope: 'all' }))} /> Todas</label>
              <label className="flex items-center gap-1.5 text-sm"><input type="radio" name="vs" checked={form.vertical_scope === 'specific'} onChange={() => setForm(f => ({ ...f, vertical_scope: 'specific' }))} /> Específicas</label>
            </div>
            {form.vertical_scope === 'specific' && (
              <div className="flex flex-wrap gap-2">
                {VERTICALS.map(v => (
                  <button key={v} onClick={() => toggleVertical(v)}
                    className={`px-3 py-1 text-xs rounded-full border transition-all ${form.allowed_verticals.includes(v) ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B] hover:border-[#0F172A]'}`}>{v}</button>
                ))}
              </div>
            )}
          </div>

          {/* Modules */}
          <div>
            <label className="text-xs font-medium text-[#64748B] mb-2 block">Módulos Incluidos</label>
            <div className="flex flex-wrap gap-2">
              {MODULES.map(m => (
                <button key={m} data-testid={`plan-mod-${m}`} onClick={() => toggleModule(m)}
                  className={`px-3 py-1 text-xs rounded-full border transition-all ${form.included_modules.includes(m) ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B] hover:border-[#0F172A]'}`}>{MODULE_LABELS[m] || m}</button>
              ))}
            </div>
          </div>

          {/* Entitlements + Limits agrupados por categoría de negocio */}
          <div className="space-y-3" data-testid="plan-groups-section">
            <label className="text-xs font-medium text-[#64748B] block">Qué incluye este plan y sus límites</label>
            {PLAN_GROUPS.map(group => {
              const isOpen = openGroups.has(group.key);
              const showAdvanced = advancedGroups.has(group.key);
              const hasAdvanced = (group.advancedEntitlements || []).length > 0 || (group.advancedLimits || []).length > 0;
              return (
                <div key={group.key} className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`plan-group-${group.key}`}>
                  <button type="button" onClick={() => toggleGroupOpen(group.key)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-colors">
                    <div className="text-left">
                      <p className="text-sm font-semibold text-[#0F172A]">{group.label}</p>
                      {group.help && <p className="text-[11px] text-[#94A3B8]">{group.help}</p>}
                    </div>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-[#94A3B8] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />}
                  </button>
                  {isOpen && (
                    <div className="p-4 space-y-4">
                      {group.entitlements.length > 0 && (
                        <div className="flex flex-wrap gap-3">
                          {group.entitlements.map(({ key, label }) => (
                            <label key={key} className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`plan-ent-${key}`}>
                              <input type="checkbox" checked={!!form.included_entitlements[key]} onChange={() => toggleEntitlement(key)} />
                              {label}
                            </label>
                          ))}
                        </div>
                      )}
                      {group.key === 'ia_conocimiento' && (
                        <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3 space-y-1.5" data-testid="plan-ai-model">
                          <p className="text-xs font-semibold text-[#0F172A]">Modelo de IA de la infraestructura CavernicolApp</p>
                          <p className="text-[11px] text-[#64748B]">El negocio usa este cerebro si no elige otro en Config IA. Tokens ilimitados: marca Ilimitado en «Uso de IA al mes».</p>
                          <select
                            className="h-8 text-xs border border-[#E2E8F0] rounded-md px-2 bg-white w-full max-w-md"
                            value={form.included_entitlements.default_ai_model_key || ''}
                            onChange={e => setPlanModel(e.target.value)}
                            data-testid="plan-default-ai-model"
                          >
                            <option value="">El de la plataforma (GPT-5.6 Luna)</option>
                            {aiModels.map(m => (
                              <option key={m.model_key || m.id} value={m.model_key}>
                                {(m.display_name || m.model_name || m.model_key)}{m.provider_key ? ` · ${m.provider_key}` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {group.limits.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {group.limits.map(({ key, label }) => renderLimitField(key, label))}
                        </div>
                      )}
                      {hasAdvanced && (
                        <div className="pt-2 border-t border-[#F1F5F9]">
                          <button type="button" onClick={() => toggleGroupAdvanced(group.key)} className="text-xs text-[#94A3B8] hover:text-[#0F172A] underline">
                            {showAdvanced ? 'Ocultar' : 'Ver'} opciones avanzadas de {group.label.toLowerCase()}
                          </button>
                          {showAdvanced && (
                            <div className="mt-3 space-y-3">
                              {group.advancedEntitlements.length > 0 && (
                                <div className="flex flex-wrap gap-3">
                                  {group.advancedEntitlements.map(({ key, label }) => (
                                    <label key={key} className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`plan-ent-${key}`}>
                                      <input type="checkbox" checked={!!form.included_entitlements[key]} onChange={() => toggleEntitlement(key)} />
                                      {label}
                                    </label>
                                  ))}
                                </div>
                              )}
                              {(group.advancedLimits || []).length > 0 && (
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                  {(group.advancedLimits || []).map(({ key, label }) => renderLimitField(key, label))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Lo que la plataforma sabe hacer y esta pantalla no mostraba.
              Sale del catálogo del backend, no de una lista escrita aquí: dos
              listas se desincronizan, y el síntoma de eso fue que 54
              capacidades —entre ellas «Usar el CRM» y los cinco giros— no se
              podían apagar desde ningún lado. Se va a vaciar sola conforme
              cada una se acomode en su grupo de arriba. */}
          {capacidadesSueltas.length > 0 && (
            <div className="mt-6 border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid="plan-capacidades-sueltas">
              <div className="px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <p className="text-sm font-semibold text-[#0F172A]">
                  Resto de capacidades ({capacidadesSueltas.reduce((n, g) => n + g.items.length, 0)})
                </p>
                <p className="text-[11px] text-[#94A3B8]">
                  Existen en el sistema y no aparecían arriba. Lo que dejes apagado, el negocio no lo verá.
                </p>
              </div>
              <div className="p-4 space-y-4">
                {capacidadesSueltas.map(g => (
                  <div key={g.modulo}>
                    <p className="text-xs font-semibold text-[#475569] mb-2">{g.nombre}</p>
                    <div className="flex flex-wrap gap-3">
                      {g.items.filter(i => i.tipo === 'booleano').map(({ clave, etiqueta, ayuda }) => (
                        <label key={clave} className="flex items-center gap-1.5 text-xs cursor-pointer" title={ayuda || ''} data-testid={`plan-ent-${clave}`}>
                          <input type="checkbox" checked={!!form.included_entitlements[clave]} onChange={() => toggleEntitlement(clave)} />
                          {etiqueta}
                        </label>
                      ))}
                    </div>
                    {g.items.some(i => i.tipo === 'limite') && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                        {g.items.filter(i => i.tipo === 'limite').map(({ clave, etiqueta }) => renderLimitField(clave, etiqueta))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Simulador: costo infra al 100% de límites vs precio de venta */}
          <PlanCostSimulator form={form} />

          <div className="flex justify-end gap-3 pt-4 border-t border-[#E2E8F0]">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="plan-form-save" className="bg-[#0F172A] text-white hover:bg-[#1E293B]">
              {saving ? 'Guardando...' : (editingPlan ? 'Actualizar' : 'Crear Plan')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="plan-builder-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Plan Builder</h1>
          <p className="text-sm text-[#64748B] mt-1">{plans.length} planes configurados</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={sembrarEscalera}
            disabled={sembrando}
            data-testid="sembrar-escalera-btn"
            variant="outline"
            title="Crea o actualiza Base, Pro, Corporativo y Enterprise, más los tres bloques de minutos de voz. No pisa precios que ya hayas ajustado."
          >
            {sembrando ? 'Cargando…' : 'Cargar escalera comercial'}
          </Button>
          <Button onClick={openCreate} data-testid="create-plan-btn" className="bg-[#0F172A] text-white hover:bg-[#1E293B]">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Plan
          </Button>
        </div>
      </div>

      {plans.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-12 text-center">
          <Package className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">No hay planes creados</p>
          <Button onClick={openCreate} variant="outline" size="sm" className="mt-4">Crear primer plan</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {plans.map(plan => (
            <div key={plan.id} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid={`plan-card-${plan.id}`}>
              <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpandedId(expandedId === plan.id ? null : plan.id)}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${plan.status === 'active' ? 'bg-emerald-500' : 'bg-[#CBD5E1]'}`} />
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-[#0F172A] truncate">{plan.name}</h3>
                    <p className="text-xs text-[#64748B]">{plan.vertical_scope === 'all' ? 'Todas las verticales' : (plan.allowed_verticals || []).join(', ')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-semibold text-[#0F172A]">${plan.price_monthly}/mes</p>
                    {plan.price_annual > 0 && <p className="text-xs text-[#64748B]">${plan.price_annual}/año</p>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); openEdit(plan); }} data-testid={`edit-plan-${plan.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleDuplicate(plan.id); }} data-testid={`dup-plan-${plan.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleToggleStatus(plan); }} data-testid={`toggle-plan-${plan.id}`}>
                      {plan.status === 'active' ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4 text-[#CBD5E1]" />}
                    </Button>
                    {isPlatformOwner && !plan.is_default && (
                      <Button variant="ghost" size="sm" disabled={deletingPlan === plan.id}
                        onClick={e => { e.stopPropagation(); handleDeletePlan(plan); }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-plan-${plan.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                  {expandedId === plan.id ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
                </div>
              </div>
              {expandedId === plan.id && (
                <div className="px-4 pb-4 border-t border-[#F1F5F9] pt-3 space-y-3">
                  {plan.description && <p className="text-xs text-[#64748B]">{plan.description}</p>}
                  <div className="flex flex-wrap gap-1.5">
                    {(plan.included_modules || []).map(m => <span key={m} className="px-2 py-0.5 bg-[#F1F5F9] text-[#475569] text-[11px] rounded">{MODULE_LABELS[m] || m}</span>)}
                  </div>
                  {Object.keys(plan.included_limits || {}).length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {Object.entries(plan.included_limits).map(([k, v]) => (
                        <div key={k} className="text-[11px]">
                          <span className="text-[#94A3B8]">{LIMIT_LABELS[k] || k}:</span>{' '}
                          <span className="font-medium text-[#0F172A]">{formatLimit(v)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 text-[11px] text-[#94A3B8]">
                    <span>Trial: {plan.trial_allowed ? `${plan.trial_days}d` : 'No'}</span>
                    <span>Ciclo: {plan.billing_cycle}</span>
                    {plan.is_default && <span className="text-[#F72585] font-medium">Predeterminado</span>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
